/**
 * Mutation Error Handler
 *
 * Provides user-friendly error messages for API mutations.
 * Strategy:
 * 1. Translate known patterns (validation, DB constraints) to Spanish
 * 2. Detect internal/database errors and show generic message
 * 3. Pass through Spanish messages from backend
 * 4. Fallback: show the raw API message (per backend team request)
 */

import type { MutationErrorOptions, ErrorInfo } from './get-mutation-error.types';
import {
  OPERATION_LABELS,
  ENTITY_ARTICLES,
  FIELD_MAPPINGS,
  INTERNAL_ERROR_PATTERNS,
} from './get-mutation-error.config';

export type { Operation, MutationErrorOptions } from './get-mutation-error.types';

/**
 * Get user-friendly error message for mutations
 * Extracts specific error details when available
 */
export function getMutationError(error: unknown, options: MutationErrorOptions): string {
  const { operation, entity } = options;
  const entityWithArticle = ENTITY_ARTICLES[entity] || `el/la ${entity}`;
  const baseMessage = `No se pudo ${OPERATION_LABELS[operation]} ${entityWithArticle}`;

  const errorInfo = extractErrorInfo(error);

  if (process.env.NODE_ENV === 'development') {
    console.error(`[MutationError] ${operation} ${entity}:`, errorInfo);
  }

  // 1. Validation errors (class-validator from NestJS)
  if (errorInfo.validationErrors.length > 0) {
    const translatedErrors = errorInfo.validationErrors
      .map(translateValidationError)
      .filter(Boolean);

    if (translatedErrors.length === 1) {
      return `${baseMessage}. ${translatedErrors[0]}`;
    }
    if (translatedErrors.length > 1) {
      return `${baseMessage}:\n• ${translatedErrors.join('\n• ')}`;
    }
  }

  // 2. Duplicate entry - extract the actual duplicated value
  if (errorInfo.isDuplicate) {
    const duplicateInfo = parseDuplicateEntry(errorInfo.message, entity);
    return duplicateInfo
      ? `Ya existe ${entityWithArticle} con ${duplicateInfo}.`
      : `Ya existe ${entityWithArticle} con esos datos.`;
  }

  // 3. Foreign key constraint - can't delete because it's referenced
  if (errorInfo.isForeignKey) {
    return `No se puede eliminar ${entityWithArticle} porque está siendo utilizado en otras partes del sistema.`;
  }

  // 4. Not found - backend sends Spanish messages for these
  if (errorInfo.status === 404 || errorInfo.isNotFound) {
    if (/no encontrad[oa]/i.test(errorInfo.message)) {
      return capitalizeFirst(errorInfo.message);
    }
    return `${capitalizeFirst(entityWithArticle)} no fue encontrado.`;
  }

  // 5. Auth errors
  if (errorInfo.status === 401) {
    return 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
  }
  if (errorInfo.status === 403) {
    return 'No tienes permisos para realizar esta acción.';
  }

  // 6. Server errors (5xx) - always generic, these are bugs
  if (errorInfo.status >= 500) {
    return `${baseMessage}. Error del servidor, intenta más tarde.`;
  }

  // 7. Network error
  if (errorInfo.isNetworkError) {
    return 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
  }

  // 8. Internal/database errors that leaked through - show generic message
  if (errorInfo.isInternalError) {
    return `${baseMessage}. Ocurrió un error interno, por favor intenta más tarde.`;
  }

  // 9. Check for known Spanish error messages from backend
  const knownMessage = extractKnownBackendMessage(errorInfo.message);
  if (knownMessage) {
    return knownMessage;
  }

  // 10. Try to translate if it looks like a validation error in the message
  const translatedMessage = tryTranslateSingleError(errorInfo.message);
  if (translatedMessage) {
    return `${baseMessage}. ${translatedMessage}`;
  }

  // 11. Final fallback: show the API message if we have one
  if (errorInfo.message && errorInfo.message.trim()) {
    const cleanedMessage = cleanBackendMessage(errorInfo.message);
    return `${baseMessage}. ${cleanedMessage}`;
  }

  return `${baseMessage}. Por favor, intenta nuevamente.`;
}

function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function extractErrorInfo(error: unknown): ErrorInfo {
  let status = 0;
  let message = '';
  let isNetworkError = false;
  let validationErrors: string[] = [];

  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as {
      isAxiosError: boolean;
      response?: { status?: number; data?: unknown };
      request?: unknown;
      message?: string;
    };
    status = axiosError.response?.status || 0;
    const data = axiosError.response?.data;

    if (data) {
      if (typeof data === 'string') {
        message = data;
      } else if (typeof data === 'object' && data !== null) {
        const dataObj = data as Record<string, unknown>;

        if (Array.isArray(dataObj.message)) {
          validationErrors = dataObj.message.filter((m): m is string => typeof m === 'string');
          message = validationErrors.join(', ');
        } else if (typeof dataObj.message === 'string') {
          message = dataObj.message;
        } else if (
          typeof dataObj.message === 'object' &&
          dataObj.message !== null &&
          'message' in (dataObj.message as object)
        ) {
          message = String((dataObj.message as { message: unknown }).message);
        } else if (typeof dataObj.error === 'string') {
          message = dataObj.error;
        } else if (
          typeof dataObj.error === 'object' &&
          dataObj.error !== null &&
          'message' in (dataObj.error as object)
        ) {
          message = String((dataObj.error as { message: unknown }).message);
        } else {
          const possibleMessage =
            dataObj.msg || dataObj.detail || dataObj.details || dataObj.reason;
          if (typeof possibleMessage === 'string') {
            message = possibleMessage;
          }
        }
      }
    }

    if (!message && axiosError.message) {
      message = axiosError.message;
    }
    if (!axiosError.response && axiosError.request) {
      isNetworkError = true;
    }
  } else if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  const msgLower = message.toLowerCase();
  const isInternalError = INTERNAL_ERROR_PATTERNS.some((pattern) => pattern.test(message));

  return {
    status,
    message: String(message),
    isDuplicate: msgLower.includes('duplicate entry') || msgLower.includes('er_dup_entry'),
    isForeignKey:
      msgLower.includes('foreign key constraint') || msgLower.includes('er_row_is_referenced'),
    isNotFound: msgLower.includes('not found') || msgLower.includes('no encontrad'),
    isNetworkError,
    isInternalError,
    validationErrors,
  };
}

function parseDuplicateEntry(message: string, entity: string): string | null {
  const match = message.match(/Duplicate entry '([^']+)' for key '([^']+)'/i);
  if (!match) return null;

  const [, value, keyName] = match;
  const keyLower = keyName.toLowerCase();

  if (value.includes('@') || keyLower.includes('email') || keyLower.includes('correo')) {
    return `ese correo electrónico (${value})`;
  }
  if (keyLower.includes('rut') || /^\d{1,2}\.\d{3}\.\d{3}-[\dkK]$/.test(value)) {
    return `ese RUT (${value})`;
  }
  if (keyLower.includes('codigo') || keyLower.includes('code')) {
    return `ese código (${value})`;
  }
  if (keyLower.includes('nombre') || keyLower.includes('name')) {
    return `ese nombre (${value})`;
  }
  if (keyLower.includes('telefono') || keyLower.includes('phone')) {
    return `ese teléfono (${value})`;
  }
  if (entity === 'usuario' && value.includes('@')) {
    return `ese correo electrónico (${value})`;
  }
  if (entity === 'cliente' || entity === 'evaluación') {
    return `esos datos (${value})`;
  }
  return `ese valor (${value})`;
}

function extractKnownBackendMessage(message: string): string | null {
  const userFriendlyPatterns = [
    /no se puede generar el informe/i,
    /la evaluación de riesgo no tiene asignado/i,
    /credenciales inválidas/i,
    /código de verificación/i,
    /contraseña incorrecta/i,
    /usuario no encontrado/i,
    /sesión expirada/i,
    /ya está asignad[oa]/i,
    /no está asignad[oa]/i,
    /no tiene .* asociad[oa]/i,
    /debe tener al menos/i,
    /ya existe/i,
    /el levantamiento no tiene/i,
    /la evaluación no tiene/i,
    /no se puede eliminar/i,
    /no se puede crear/i,
    /no se puede actualizar/i,
    /con ID \d+ no encontrad/i,
    /no fue encontrad/i,
  ];

  for (const pattern of userFriendlyPatterns) {
    if (pattern.test(message)) {
      return capitalizeFirst(message);
    }
  }
  return null;
}

function tryTranslateSingleError(message: string): string | null {
  const validationPatterns = [
    /must be/i,
    /should not be empty/i,
    /should be/i,
    /is not valid/i,
    /is required/i,
    /cannot be/i,
  ];

  const isLikelyValidation = validationPatterns.some((p) => p.test(message));
  if (!isLikelyValidation) return null;

  return translateValidationError(message);
}

function cleanBackendMessage(message: string): string {
  const wrappedMatch = message.match(/^Error al \w+ \w+:\s*(.+)$/i);
  if (wrappedMatch) {
    const innerError = wrappedMatch[1];
    const isInnerInternal = INTERNAL_ERROR_PATTERNS.some((p) => p.test(innerError));
    if (isInnerInternal) {
      return 'Ocurrió un error interno, por favor intenta más tarde.';
    }
    const translated = tryTranslateSingleError(innerError);
    if (translated) {
      return translated;
    }
    return capitalizeFirst(innerError);
  }
  return capitalizeFirst(message);
}

function translateValidationError(error: string): string {
  const errorLower = error.toLowerCase();
  const fieldMatch = error.match(/^(\w+)\s+(.*)/);
  const fieldName = fieldMatch?.[1] || '';
  const fieldTranslated = translateFieldName(fieldName);

  if (
    errorLower.includes('must be shorter than') ||
    errorLower.includes('must be at most') ||
    errorLower.includes('shorter than or equal')
  ) {
    const lengthMatch = error.match(/(\d+)\s*characters/);
    const limit = lengthMatch?.[1];
    if (limit) return `${fieldTranslated} debe tener máximo ${limit} caracteres.`;
  }

  if (
    errorLower.includes('must be longer than') ||
    errorLower.includes('must be at least') ||
    errorLower.includes('longer than or equal')
  ) {
    const lengthMatch = error.match(/(\d+)\s*characters/);
    const limit = lengthMatch?.[1];
    if (limit) return `${fieldTranslated} debe tener mínimo ${limit} caracteres.`;
  }

  if (
    errorLower.includes('should not be empty') ||
    errorLower.includes('must not be empty') ||
    errorLower.includes('is required') ||
    errorLower.includes('cannot be empty')
  ) {
    return `${fieldTranslated} es obligatorio.`;
  }

  if (
    errorLower.includes('must be an email') ||
    errorLower.includes('must be a valid email') ||
    errorLower.includes('is not a valid email')
  ) {
    return `${fieldTranslated} debe ser un correo electrónico válido.`;
  }

  if (
    errorLower.includes('must be a number') ||
    errorLower.includes('must be an integer') ||
    errorLower.includes('must be a decimal') ||
    errorLower.includes('is not a number')
  ) {
    return `${fieldTranslated} debe ser un número.`;
  }

  if (errorLower.includes('must be a string') || errorLower.includes('expected string')) {
    return `${fieldTranslated} debe ser texto.`;
  }

  if (errorLower.includes('must be a positive number') || errorLower.includes('must be positive')) {
    return `${fieldTranslated} debe ser un número positivo.`;
  }

  if (errorLower.includes('must be a negative number')) {
    return `${fieldTranslated} debe ser un número negativo.`;
  }

  if (
    errorLower.includes('must not be less than') ||
    errorLower.includes('must be greater than or equal')
  ) {
    const minMatch = error.match(/(?:less than|equal to)\s*(\d+)/);
    const minValue = minMatch?.[1];
    return `${fieldTranslated} debe ser mayor o igual a ${minValue || 'el mínimo'}.`;
  }

  if (
    errorLower.includes('must not be greater than') ||
    errorLower.includes('must be less than or equal')
  ) {
    const maxMatch = error.match(/(?:greater than|equal to)\s*(\d+)/);
    const maxValue = maxMatch?.[1];
    return `${fieldTranslated} debe ser menor o igual a ${maxValue || 'el máximo'}.`;
  }

  if (errorLower.includes('must be an array')) {
    return `${fieldTranslated} debe ser una lista.`;
  }
  if (errorLower.includes('must contain at least')) {
    const sizeMatch = error.match(/at least (\d+)/);
    return `${fieldTranslated} debe contener al menos ${sizeMatch?.[1] || '1'} elemento(s).`;
  }
  if (errorLower.includes('must contain not more than')) {
    const sizeMatch = error.match(/not more than (\d+)/);
    return `${fieldTranslated} debe contener máximo ${sizeMatch?.[1]} elemento(s).`;
  }

  if (errorLower.includes('must be a boolean') || errorLower.includes('must be true or false')) {
    return `${fieldTranslated} debe ser verdadero o falso.`;
  }

  if (
    errorLower.includes('must be a date') ||
    errorLower.includes('must be a valid date') ||
    errorLower.includes('is not a valid date')
  ) {
    return `${fieldTranslated} debe ser una fecha válida.`;
  }

  if (
    errorLower.includes('must be a url') ||
    errorLower.includes('must be an url') ||
    errorLower.includes('must be a valid url')
  ) {
    return `${fieldTranslated} debe ser una URL válida.`;
  }

  if (errorLower.includes('must be a uuid') || errorLower.includes('must be an uuid')) {
    return `${fieldTranslated} tiene un formato de identificador inválido.`;
  }

  if (errorLower.includes('must be one of') || errorLower.includes('must be a valid enum value')) {
    return `${fieldTranslated} tiene un valor no permitido.`;
  }

  if (errorLower.includes('must match')) {
    return `${fieldTranslated} tiene un formato inválido.`;
  }

  if (errorLower.includes('must be a valid phone number')) {
    return `${fieldTranslated} debe ser un número de teléfono válido.`;
  }

  if (errorLower.includes('must be a credit card')) {
    return `${fieldTranslated} debe ser un número de tarjeta válido.`;
  }

  if (errorLower.includes('must be')) {
    return `${fieldTranslated} tiene un formato inválido.`;
  }

  if (errorLower.includes('is not valid') || errorLower.includes('is invalid')) {
    return `${fieldTranslated} no es válido.`;
  }

  return capitalizeFirst(error);
}

function translateFieldName(field: string): string {
  if (FIELD_MAPPINGS[field]) {
    return FIELD_MAPPINGS[field];
  }

  const fieldLower = field.toLowerCase();
  for (const [key, value] of Object.entries(FIELD_MAPPINGS)) {
    if (key.toLowerCase() === fieldLower) {
      return value;
    }
  }

  const readable = field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

  return `El campo "${readable}"`;
}
